import React, { createContext, useState, useContext, useEffect } from 'react';
import { I18n } from 'i18n-js';
import { storageService } from '../utils/storageService';

const LanguageContext = createContext(undefined);

export const LanguageProvider = ({ children }) => {
  // Initialize language from storageService
  const [language, setLanguage] = useState(() => {
    return storageService.getLanguagePreference() || 'gr';
  });

  // Update i18n locale when language changes
  useEffect(() => {
    i18n.locale = language;
  }, [language]);

  const toggleLanguage = () => {
    setLanguage((prev) => {
      const newLang = prev === 'gr' ? 'en' : 'gr';
      
      // Save to storageService
      storageService.setLanguagePreference(newLang);
      
      // Update i18n locale
      i18n.locale = newLang;
      
      console.log(`🌐 Language changed to: ${newLang}`);
      
      return newLang;
    });
  };

  const setLanguageDirectly = (newLanguage) => {
    if (newLanguage && (newLanguage === 'gr' || newLanguage === 'en')) {
      setLanguage(newLanguage);
      
      // Save to storageService
      storageService.setLanguagePreference(newLanguage);
      
      // Update i18n locale
      i18n.locale = newLanguage;
      
      console.log(`🌐 Language set to: ${newLanguage}`);
    }
  };

  return (
    <LanguageContext.Provider value={{ 
      language, 
      toggleLanguage, 
      setLanguage: setLanguageDirectly 
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const i18n = new I18n({
  en: {
    home: 'Home',
    homefade: '',
    mapScreen: 'Map',
    companies: 'Exhibitors',
    registrationForm: 'Registration Form',
    exhibits: 'Exhibits',
    pavilions: 'Pavilions',
    scanQrCode: 'Scan QR',
    welcomeText: 'Welcome to the Exhibition Portal!',
    welcomeExhibitionText: 'Welcome to',
    instructionText: 'Enjoy the experience',
    logout: 'Logout',
    exhibitorsList: 'Exhibitors List',
    exhibitorsDetails: 'Exhibitor Details',
    pavilionList: 'Pavilions List',
    pavilionExhibits: 'Pavilion Details',
    exhibitsList: 'Exhibits List',
    exhibitsDetails:'Exhibitors Associated with the exhibit',
    exhibitorsExhibits:'Exhibitor',
    ticketButton:'Tickets',
    detailsButton:'Details',
    country: "Country ",
    fullName: "Full Name ",
    company: "Company ",
    companyActivity: "Company Activity ",
    jobDescription: "Job Description ",
    city: "City *",
    email: "Email Address ",
    phone: "Telephone ",
    profileButton:'Profile',
    chooseAnotherExhibition:'Choose Exhibition',
    favoriteExhibits: "Favorite Exhibits",
    termsAccept: "I accept the terms regarding my personal data and Would you like to receive emails or mail for this exhibition?",
    submit: "Submit",
    county: "County ",
    login: 'Login',
    vat: "VAT Number ",
    submissionSuccess:'Registration has completed successfully',
    selectJob: "Select Job Position",
    selectCompanyActivity: "Select Company Activity",
    failedFetchJobs: "Failed to load job positions",
    failedFetchCompanyActivities: "Failed to load company activities",
    selectCountry:"Select Country",
    submissionError:"Unsuccessfull Submission",
    success: 'Approved',
    selectCounty:"Select County",
    QrScreen: "My QR Code",
    submitting: "Submitting...",
    registrationButton: "Register",
    Profil: "Profil",
    pleaseSpecify: "PleaseSpecify",
    info: 'Info',
    events: 'Events',
    exhibitionInfo: 'Exhibition Information',
    exhibitionEvents: 'Exhibition Events',
    noInfoAvailable: 'No information available for this exhibition',
    noEventsAvailable: 'No events available for this exhibition',
    loadingInfo: 'Loading information...',
    loadingEvents: 'Loading events...',
    searchEvents: 'Search events...',
    today: 'Today',
    upcoming: 'Upcoming',
    past: 'Past',
    register: 'Register',
    organizedBy: 'Organized by',
    eventDetails: 'Event Details',
    exhibitionView: 'View All Exhibitions',
    // Ticket translations
    servicesButton: 'Services',
    servicesTitle: 'Exhibition Services',
    servicesDescription: 'Access services for the exhibition',
    loadingServices: 'Loading services...',
    serviceSystemError: 'Unable to load service system. Please try again later.',
    ticketsTitle: 'Exhibition Tickets',
    ticketsDescription: 'Purchase tickets for the exhibition',
    loadingTickets: 'Loading tickets...',
    ticketSystemError: 'Unable to load ticket system. Please try again later.',
    closeTickets: 'Close',
    disclaimer:`IMPORTANT INFORMATION CONCERNING YOUR PERSONAL DATA

TIF HELEXPO receives your data which are kept in an (electronic and/or physical) archive under its responsibility, taking all the appropriate security measures. We would like to inform you that we will use the data we request solely and exclusively in order to manage and discharge our contractual and legal obligations. We are obligated to retain these data for the minimum period required by the legislation in force in order to discharge our obligations to third parties (including tax authorities) for the period laid down in the relevant provisions. Should the above limitations cease to apply, we will permanently delete your data after having previously notified you. You have the right to access, rectification and erasure of your personal data, as well as the right to their portability for transmission to a different Controller or Processor by sending a relevant request to the Technical Studies Project and Monitoring ISO & GDPR Department, or an e-mail message to the address dpo@helexpo.gr. You also have the right to withdraw your consent for the processing of your data on our part. In this event, please bear in mind that this withdrawal may automatically entail the cessation of our cooperation, given the necessity of processing your data in order to perform our contractual obligations. In any event, we may request your identity data in order to make sure that we are not communicating your personal data to anyone else. We will take every possible measure to satisfy your requests within thirty days from receiving them, as we are obligated to do, notifying you of the completion of your request or the reasons hindering the exercise of your rights or the satisfaction of one or more of your rights, as well as the reasons any delay on our part beyond thirty days. Additionally, we will notify you of your further rights in the event of undue response on our part to your requests. If you feel that your rights with respect to your personal data are being harmed, you always have the right to lodge a complaint with the competent Supervisory Personal Data Protection Authority: http://www.dpa.gr, 1-3, Kifissias Street, GR-11523, Athens, Tel.: 210 6475600, e-mail: contact@dpa.gr. In this event, we would particularly appreciate your prior communication with our Data Protection Officer (DPO) either by sending a physical letter to the company headquarters (TIF-HELEXPO 154, EGNATIA str, 54636) or by sending an e-mail message (to the e-mail address: dpo@helexpo.gr ), always including your full particulars and the reason for communicating with the company. You can find our Personal Data Protection Policy on our website, https://www.helexpo.gr/en/GDPR_Policy.`
  },
  gr: {
    home: 'Αρχική',
    homefade: '',
    mapScreen: 'Χάρτης',
    companies: 'Εκθέτες',
    registrationForm: 'Φόρμα Εγγραφής',
    exhibits: 'Εκθέματα',
    pavilions: 'Περίπτερα',
    scanQrCode: 'Σάρωση QR',
    welcomeText: 'Καλώς ήρθατε στην Πλατφόρμα Εκθέσεων!',
    welcomeExhibitionText: 'Καλώς ήρθατε στην',
    instructionText: 'Απολαύστε την εμπειρία',
    logout: 'Αποσύνδεση',
    exhibitorsList:'Λίστα Εκθετών',
    exhibitorsDetails: 'Λεπτομέρειες Εκθέτη',
    pavilionList: 'Λίστα Περιπτέρων',
    pavilionExhibits: 'Περιεχόμενα Περιπτέρου',
    exhibitsList: 'Λίστα Εκθεμάτων',
    exhibitsDetails:'Εκθέτες που σχετιζόνται με το έκθεμα',
    exhibitorsExhibits:'Εκθέτης',
    country: "Χώρα ",
    fullName: "Ονοματεπώνυμο ",
    company: "Εταιρία ",
    companyActivity: "Αντικείμενο Εταιρίας ",
    jobDescription: "Θέση στη Εταιρία ",
    city: "Πόλη ",
    email: "Διεύθυνση Email ",
    phone: "Τηλέφωνο",
    ticketButton: 'Εισιτήρια',
    detailsButton:'Πληροφορίες',
    profileButton: 'Προφίλ',
    chooseAnotherExhibition: 'Επιλέξτε Έκθεση',
    favoriteExhibits: "Αγαπημένα Εκθέματα",
    termsAccept: "Αποδέχομαι τους όρους σχετικά με τα προσωπικά μου δεδομένα. Θα θέλατε να λαμβάνετε emails ή αλληλογραφία για αυτήν την έκθεση;",
    submit: "Υποβολή",
    county: "Νομός ",
    success: 'Εγκρίθηκε',
    submissionSuccess:'Η εγγραφή ολοκληρώθηκε με επιτυχία',
    submissionError:'Η εγγραφή ήταν ανεπιτυχής',
    QrScreen: "My QR Code",
    registrationButton:"Εγγραφή",
    selectCounty:"Επιλέξτε Νομό",
    vat: "ΑΦΜ ",
    login: 'Είσοδος',
    Profil: "Προφίλ",
    info: 'Πληροφορίες',
    events: 'Εκδηλώσεις',
    exhibitionInfo: 'Πληροφορίες Έκθεσης',
    exhibitionEvents: 'Εκδηλώσεις Έκθεσης',
    noInfoAvailable: 'Δεν υπάρχουν διαθέσιμες πληροφορίες για αυτήν την έκθεση',
    noEventsAvailable: 'Δεν υπάρχουν διαθέσιμες εκδηλώσεις για αυτήν την έκθεση',
    loadingInfo: 'Φόρτωση πληροφοριών...',
    loadingEvents: 'Φόρτωση εκδηλώσεων...',
    searchEvents: 'Αναζήτηση εκδηλώσεων...',
    today: 'Σήμερα',
    upcoming: 'Προσεχώς',
    past: 'Παρελθόν',
    register: 'Εγγραφή',
    organizedBy: 'Διοργανωτής',
    eventDetails: 'Λεπτομέρειες Εκδήλωσης',
    pleaseSpecify: "Παρακαλώ Περιγράψτε",
    selectJob: "Επιλέξτε Θέση Εργασίας",
    selectCompanyActivity: "Επιλέξτε Δραστηριότητα Εταιρείας",
    failedFetchJobs: "Αποτυχία φόρτωσης θέσεων εργασίας",
    failedFetchCompanyActivities: "Αποτυχία φόρτωσης δραστηριοτήτων εταιρείας",
    exhibitionView: "Προβολη Έκθεσης",
    selectCountry: "Επελέξε Χώρα",
    submitting: "Υποβάλλεται...",
    // Ticket translations
    servicesButton: 'Υπηρεσίες',
    servicesTitle: 'Υπηρεσίες Έκθεσης',
    servicesDescription: 'Αγοράστε υπηρεσίες για την έκθεση',
    loadingServices: 'Φόρτωση υπηρεσίων...',
    serviceSystemError: 'Αδυναμία φόρτωσης συστήματος υπηρεσίων. Παρακαλώ δοκιμάστε ξανά αργότερα.',
    ticketsTitle: 'Εισιτήρια Έκθεσης',
    ticketsDescription: 'Αγοράστε εισιτήρια για την έκθεση',
    loadingTickets: 'Φόρτωση εισιτηρίων...',
    ticketSystemError: 'Αδυναμία φόρτωσης συστήματος εισιτηρίων. Παρακαλώ δοκιμάστε ξανά αργότερα.',
    closeTickets: 'Κλείσιμο',
    disclaimer: `ΣΗΜΑΝΤΙΚΗ ΕΝΗΜΕΡΩΣΗ ΣΧΕΤΙΚΑ ΜΕ ΤΑ ΠΡΟΣΩΠΙΚΑ ΣΑΣ ΔΕΔΟΜΕΝΑ

Η HELEXPO ΔΕΘ λαμβάνει τα δεδομένα σας τα οποία φυλάσσονται σε (ηλεκτρονικό ή/και φυσικό) αρχείο υπ' ευθύνη της, τηρώντας όλα τα κατάλληλα μέτρα ασφάλειας. Σας ενημερώνουμε, ότι θα χρησιμοποιήσουμε τα δεδομένα που σας ζητάμε αποκλειστικά και μόνο για τη διαχείριση και διεκπεραίωση των συμβατικών μας αλλά και εκ του νόμου απορρεουσών υποχρεώσεών μας. Τα δεδομένα αυτά υποχρεούμαστε να διατηρήσουμε για την ελάχιστη περίοδο που απαιτείται από την υπάρχουσα νομοθεσία, ώστε να εκπληρωθούν οι υποχρεώσεις μας προς τρίτα μέρη (συμπεριλαμβανομένων των φορολογικών αρχών) για το χρονικό διάστημα που προβλέπουν οι σχετικές διατάξεις. Εφόσον δεν υφίστανται οι ανωτέρω περιορισμοί, θα διαγράψουμε οριστικά τα δεδομένα σας αφού προηγηθεί σχετική ενημέρωσή σας. Έχετε το δικαίωμα πρόσβασης, διόρθωσης και διαγραφής των προσωπικών δεδομένων σας καθώς και το δικαίωμα φορητότητας αυτών προς μεταφορά σε άλλο Υπεύθυνο ή Εκτελούντα Επεξεργασία, αποστέλλοντας σχετικό αίτημά σας στο Τμήμα Τεχνικών Μελετών Έργων και Παρακολούθησης Πιστοποιήσεων (ISO & GDPR) Εγνατία 154 | ΤΚ 546 36 | Θεσσαλονίκη |, ή με email στην ηλεκτρονική διεύθυνση dpo@helexpo.gr Επίσης έχετε το δικαίωμα να αποσύρετε την συγκατάθεσή σας για την επεξεργασία εκ μέρους μας των δεδομένων σας. Στην περίπτωση αυτή παρακαλούμε να έχετε υπόψη σας, ότι μία τέτοια ανάκληση ενδέχεται να σημαίνει αυτομάτως και τη διακοπή της συνεργασίας μας, δεδομένης της αναγκαιότητας της επεξεργασίας των δεδομένων σας για την εκτέλεση των συμβατικών μας υποχρεώσεων. Σε κάθε περίπτωση, ενδέχεται να σας ζητήσουμε τα στοιχεία της ταυτότητάς σας ώστε να βεβαιωθούμε ότι δεν γνωστοποιούμε σε κάποιον άλλον τα προσωπικά σας στοιχεία. Θα λάβουμε κάθε δυνατό μέτρο για την ικανοποίηση των αιτημάτων σας εντός τριάντα ημερών από τη λήψη του, ως οφείλουμε, ενημερώντας σας για την ικανοποίηση του αιτήματός σας, ή για τους λόγους που εμποδίζουν την εκ μέρους σας άσκηση ή την ικανοποίηση ενός ή και περισσότερων από τα δικαιώματά σας, καθώς και για τους λόγους τυχόν καθυστέρησής μας πέραν του διαστήματος των τριάντα ημερών. Επίσης θα σας ενημερώσουμε για τα περαιτέρω δικαιώματά σας σε περίπτωση μη προσήκουσας ανταπόκρισής μας στα αιτήματά σας. Εφόσον αισθάνεστε ότι θίγονται τα δικαιώματά σας επί των προσωπικών σας δεδομένων, διατηρείτε πάντοτε το δικαίωμα να υποβάλετε παράπονο/καταγγελία στην αρμόδια Εποπτική Αρχή Προστασίας Προσωπικών Δεδομένων: http://www.dpa.gr, Κηφισίας 1-3, Τ.Κ. 115 23, Αθήνα, τηλ. 210 6475600, email:contact@dpa.gr. Στην περίπτωση αυτή, θα εκτιμούσαμε ιδιαίτερα αν προηγούνταν επικοινωνία σας με τον Υπεύθυνό μας Προστασίας Προσωπικών Δεδομένων (DPO), είτε μέσω αποστολής Φυσικής Επιστολής στην έδρα της εταιρίας (Δ.Ε.Θ.- HELEXPO Α.Ε., ΕΓΝΑΤΙΑΣ 154, 54633), είτε μέσω αποστολής Ηλεκτρονικής Επιστολής (στο λογαριασμό Ηλεκτρονικό Ταχυδρομείο: dpo@helexpo.gr), αναφέροντας πάντοτε τα πλήρη στοιχεία σας και το λόγο επικοινωνίας σας με την εταιρία. Μπορείτε να βρείτε αναλυτικά την Πολιτική μας για την Προστασία Προσωπικών Δεδομένων στον ιστότοπό μας https://www.helexpo.gr/el/GDPR_Policy`
  },
});

// Initialize with default language
i18n.locale = 'gr';

export default i18n;